import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchLeaveTypes, fetchLeaveRequests, fetchLeaveBalances, fetchHolidays,
  fetchHolidayCalendars, createHolidayCalendarApi,
  submitLeaveRequest as submitLeaveApi, decideLeaveRequest, cancelLeaveRequest, addHolidayApi,
  ApiError,
} from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Button from '../components/Button';
import ProgressBar from '../components/ProgressBar';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Alert from '../components/Alert';
import DatePicker from '../components/DatePicker';

export default function Leave() {
  const { role, profileId, employeeId, modal, openModal, closeModal, toast } = useAppStore();
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();

  const [leaveForm, setLeaveForm] = useState({ leaveTypeId: '', startDate: '', endDate: '', reason: '' });
  const [rejectState, setRejectState] = useState<{ id: string; reason: string } | null>(null);
  const [holidayForm, setHolidayForm] = useState({ date: '', name: '', type: 'national' as 'national' | 'regional' | 'optional' });

  const { data: leaveTypes = [] } = useQuery({ queryKey: qk.leaveTypes(), queryFn: fetchLeaveTypes });
  const { data: leaveRequests = [] } = useQuery({
    queryKey: qk.leaveRequests(role === 'ess' ? myId : undefined),
    queryFn: () => fetchLeaveRequests(role === 'ess' ? myId : undefined),
    enabled: role !== 'ess' || !!employeeId,
  });
  const { data: leaveBalances = [] } = useQuery({
    queryKey: qk.leaveBalances(myId),
    queryFn: () => fetchLeaveBalances(myId),
    enabled: !!employeeId,
  });
  const { data: holidays = [] } = useQuery({ queryKey: qk.holidays(), queryFn: fetchHolidays });
  const { data: holidayCalendars = [] } = useQuery({
    queryKey: qk.holidayCalendars(),
    queryFn: fetchHolidayCalendars,
    enabled: role === 'hr',
  });

  // Balance cards: join leaveTypes with balances
  const balanceCards = leaveTypes.map((lt) => ({
    ...lt,
    balance: leaveBalances.find((b) => b.leaveTypeId === lt.id)?.balance ?? 0,
    credited: leaveBalances.find((b) => b.leaveTypeId === lt.id)?.credited ?? 0,
    debited: leaveBalances.find((b) => b.leaveTypeId === lt.id)?.debited ?? 0,
  }));

  const submitMutation = useMutation({
    mutationFn: submitLeaveApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveRequests(role === 'ess' ? myId : undefined) });
      toast('Leave request submitted');
      closeModal();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'INSUFFICIENT_BALANCE') {
        toast('Insufficient leave balance for the selected dates', 'danger');
        return;
      }
      toast('Failed to submit leave request', 'danger');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => decideLeaveRequest(id, 'approved'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveRequests(role === 'ess' ? myId : undefined) });
      toast('Leave request approved');
    },
    onError: () => toast('Failed to approve', 'danger'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => decideLeaveRequest(id, 'rejected', reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveRequests(role === 'ess' ? myId : undefined) });
      toast('Leave request rejected', 'warn');
      setRejectState(null);
    },
    onError: () => toast('Failed to reject', 'danger'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelLeaveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveRequests(role === 'ess' ? myId : undefined) });
      toast('Leave request cancelled');
    },
    onError: () => toast('Failed to cancel leave request', 'danger'),
  });

  const addHolidayMutation = useMutation({
    mutationFn: async (input: { holidayDate: string; name: string; type: 'national' | 'regional' | 'optional' }) => {
      const calendarId = holidays[0]?.calendarId
        ?? holidayCalendars[0]?.id
        ?? (await createHolidayCalendarApi('Default calendar')).id;
      return addHolidayApi({ ...input, calendarId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.holidays() });
      queryClient.invalidateQueries({ queryKey: qk.holidayCalendars() });
      toast('Holiday added');
      setHolidayForm({ date: '', name: '', type: 'national' });
      closeModal();
    },
    onError: () => toast('Failed to add holiday', 'danger'),
  });

  const pendingForApproval = leaveRequests.filter((r) => r.status === 'submitted');
  const historyRequests = role === 'ess'
    ? leaveRequests.filter((r) => r.employeeId === myId)
    : leaveRequests;

  const today = new Date().toISOString().slice(0, 10);

  const calcDays = () => {
    if (!leaveForm.startDate || !leaveForm.endDate) return 0;
    let count = 0;
    const cur = new Date(leaveForm.startDate + 'T00:00:00');
    const end = new Date(leaveForm.endDate + 'T00:00:00');
    while (cur <= end) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  // Auto-set first leave type when types load
  const effectiveLeaveTypeId = leaveForm.leaveTypeId || leaveTypes[0]?.id || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      <div className="sk-page-header" style={{ flexShrink: 0 }}>
        <h1>Leave</h1>
        {role === 'ess' && (
          <Button variant="accent" onClick={() => openModal('apply_leave')}>
            <Icon name="plus" size={14} color="white" />
            Apply Leave
          </Button>
        )}
      </div>

      {/* Balances */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24, flexShrink: 0 }}>
        {balanceCards.map((b) => (
          <div key={b.id} className="leave-bal-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div className="leave-bal-name" style={{ flex: 1 }}>{b.name}</div>
              {b.code && <Chip variant="neutral">{b.code}</Chip>}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
              <span className="leave-bal-count">{b.balance}</span>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>/ {b.annualDays} days</span>
            </div>
            <ProgressBar value={Number(b.balance)} max={b.annualDays} variant={Number(b.balance) < 3 ? 'warn' : 'accent'} style={{ marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Credited: {b.credited}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>·</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Used: {b.debited}</span>
              {b.encashable && <Chip variant="accent">Encashable</Chip>}
            </div>
          </div>
        ))}
      </div>

      {/* Pending approvals — MSS/HR only */}
      {(role === 'mss' || role === 'hr') && pendingForApproval.length > 0 && (
        <div className="t-card" style={{ marginBottom: 20, flexShrink: 0 }}>
          <div className="card-head">
            <h3>Pending approvals</h3>
            <span style={{ background: 'var(--warn-soft, #fef3c7)', color: 'var(--warn, #d97706)', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 8px' }}>
              {pendingForApproval.length}
            </span>
          </div>
          <table className="t-table">
            <thead><tr><th>Employee</th><th>Leave type</th><th>Dates</th><th>Days</th><th>Reason</th><th></th></tr></thead>
            <tbody>
              {pendingForApproval.map((r) => {
                const lt = leaveTypes.find((l) => l.id === r.leaveTypeId);
                const empName = r.employee ? `${r.employee.firstName} ${r.employee.lastName}`.trim() : r.employeeId;
                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar firstName={r.employee?.firstName ?? ''} lastName={r.employee?.lastName} color={r.employee?.avatarColor} size="sm" />
                          <span>{empName}</span>
                        </div>
                      </td>
                      <td>{lt?.name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {new Date(r.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} → {new Date(r.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td>{r.days}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button variant="success" size="sm" onClick={() => approveMutation.mutate(r.id)}>Approve</Button>
                          <Button variant="danger" size="sm" onClick={() => setRejectState(rejectState?.id === r.id ? null : { id: r.id, reason: '' })}>
                            {rejectState?.id === r.id ? 'Cancel' : 'Reject'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {rejectState?.id === r.id && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--surface-raised)', padding: '10px 16px' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="t-input" placeholder="Reason for rejection (required)…" value={rejectState.reason} autoFocus
                              onChange={(e) => setRejectState((s) => s ? { ...s, reason: e.target.value } : s)} style={{ flex: 1 }} />
                            <Button variant="danger" size="sm" onClick={() => {
                              if (!rejectState.reason.trim()) { toast('Enter a rejection reason', 'warn'); return; }
                              rejectMutation.mutate({ id: r.id, reason: rejectState.reason.trim() });
                            }}>Confirm reject</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Two-column: history (left) + upcoming holidays (right) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, flex: 1, minHeight: 0 }}>

        {/* Leave history */}
        <div className="t-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div className="card-head" style={{ flexShrink: 0 }}>
            <h3>{role === 'ess' ? 'My leave history' : 'Leave requests'}</h3>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <table className="t-table">
            <thead>
              <tr>
                {role !== 'ess' && <th>Employee</th>}
                <th>Type</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Status</th>
                <th>Applied</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {historyRequests.length === 0 ? (
                <tr><td colSpan={role !== 'ess' ? 7 : 6} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>No leave requests yet</td></tr>
              ) : historyRequests.map((r) => {
                const lt = leaveTypes.find((l) => l.id === r.leaveTypeId);
                const empName = r.employee ? `${r.employee.firstName} ${r.employee.lastName ?? ''}`.trim() : '—';
                const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      {role !== 'ess' && (
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar firstName={r.employee?.firstName ?? ''} lastName={r.employee?.lastName} color={r.employee?.avatarColor} size="sm" />
                            <span>{empName}</span>
                          </div>
                        </td>
                      )}
                      <td>{lt?.name ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.startDate)} → {fmtDate(r.endDate)}</td>
                      <td>{r.days}</td>
                      <td>
                        <Chip variant={r.status === 'approved' ? 'success' : r.status === 'submitted' ? 'warn' : r.status === 'rejected' ? 'danger' : 'neutral'} dot>
                          {r.status}
                        </Chip>
                      </td>
                      <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{r.createdAt.slice(0, 10)}</td>
                      <td>
                        {role === 'ess' && r.status === 'submitted' && (
                          <Button variant="ghost" size="sm" onClick={() => cancelMutation.mutate(r.id)}>Cancel</Button>
                        )}
                        {role !== 'ess' && r.status === 'submitted' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Button variant="success" size="sm" onClick={() => approveMutation.mutate(r.id)}>Approve</Button>
                            <Button variant="danger" size="sm" onClick={() => setRejectState(rejectState?.id === r.id ? null : { id: r.id, reason: '' })}>
                              {rejectState?.id === r.id ? 'Cancel' : 'Reject'}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {rejectState?.id === r.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--warn-soft)', padding: '10px 16px' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="t-input" placeholder="Reason for rejection (required)…" value={rejectState.reason} autoFocus
                              onChange={(e) => setRejectState((s) => s ? { ...s, reason: e.target.value } : s)} style={{ flex: 1 }} />
                            <Button variant="danger" size="sm" onClick={() => {
                              if (!rejectState.reason.trim()) { toast('Enter a rejection reason', 'warn'); return; }
                              rejectMutation.mutate({ id: r.id, reason: rejectState.reason.trim() });
                            }}>Confirm reject</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Upcoming holidays — redesigned side panel */}
        <div className="t-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div className="card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <h3>Holiday calendar</h3>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>{new Date().getFullYear()} · upcoming</div>
            </div>
            {role === 'hr' && (
              <Button variant="outline" size="sm" onClick={() => openModal('add_holiday')}>
                <Icon name="plus" size={13} color="currentColor" /> Add
              </Button>
            )}
          </div>
          <div style={{ padding: '4px 0 8px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {(() => {
              const upcoming = holidays.filter(h => h.date >= today).slice(0, 12);
              if (upcoming.length === 0) return (
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', padding: '12px 20px' }}>No upcoming holidays.</p>
              );
              // Group by month
              const byMonth: Record<string, typeof upcoming> = {};
              upcoming.forEach(h => {
                const key = h.date.slice(0, 7);
                if (!byMonth[key]) byMonth[key] = [];
                byMonth[key].push(h);
              });
              const TYPE_COLOR: Record<string, string> = {
                national: 'var(--accent)',
                regional: 'var(--success)',
                optional: 'var(--warn)',
              };
              return Object.entries(byMonth).map(([month, items]) => {
                const [y, m] = month.split('-').map(Number);
                const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                return (
                  <div key={month} style={{ marginBottom: 4 }}>
                    <div style={{ padding: '8px 20px 4px', fontSize: 'var(--fs-xs)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                      {monthLabel}
                    </div>
                    {items.map(h => {
                      const d = new Date(h.date + 'T00:00:00');
                      const dayNum = d.toLocaleDateString('en-IN', { day: 'numeric' });
                      const dayName = d.toLocaleDateString('en-IN', { weekday: 'short' });
                      const isPast = h.date < today;
                      return (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px', opacity: isPast ? 0.45 : 1, transition: 'background 0.1s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--neutral-soft)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          {/* Date badge */}
                          <div style={{ width: 38, flexShrink: 0, textAlign: 'center', padding: '5px 4px', borderRadius: 8, background: 'var(--neutral-soft)', border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.1 }}>{dayNum}</div>
                            <div style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{dayName}</div>
                          </div>
                          {/* Left accent bar + name */}
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 3, height: 28, borderRadius: 99, background: TYPE_COLOR[h.type] ?? 'var(--border)', flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textTransform: 'capitalize', marginTop: 1 }}>{h.type}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>

      </div>

      {/* Add Holiday Modal */}
      {modal.type === 'add_holiday' && (
        <Modal
          title="Add holiday"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal} disabled={addHolidayMutation.isPending}>Cancel</Button>
              <Button
                variant="accent"
                disabled={addHolidayMutation.isPending}
                onClick={() => {
                  if (!holidayForm.date || !holidayForm.name) {
                    toast('Please fill in date and name', 'warn'); return;
                  }
                  addHolidayMutation.mutate({
                    holidayDate: holidayForm.date,
                    name: holidayForm.name,
                    type: holidayForm.type,
                  });
                }}
              >
                {addHolidayMutation.isPending ? 'Saving…' : 'Add holiday'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Date */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text, #1f2937)' }}>Date</label>
              <DatePicker value={holidayForm.date} onChange={(v) => setHolidayForm((f) => ({ ...f, date: v }))} variant="pill" />
            </div>

            {/* Holiday name */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text, #1f2937)' }}>Holiday name</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 16, pointerEvents: 'none', display: 'flex' }}>
                  <Icon name="star" size={18} color="#9ca3af" />
                </span>
                <input
                  type="text"
                  placeholder="e.g. Diwali"
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
                  style={{
                    width: '100%',
                    paddingLeft: 48,
                    paddingRight: 16,
                    paddingTop: 14,
                    paddingBottom: 14,
                    background: '#f3f4f6',
                    border: '1.5px solid transparent',
                    borderRadius: 14,
                    fontSize: 15,
                    color: 'var(--text, #1f2937)',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Type */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text, #1f2937)' }}>Type</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 16, pointerEvents: 'none', display: 'flex', zIndex: 1 }}>
                  {/* Globe icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                </span>
                <select
                  value={holidayForm.type}
                  onChange={(e) => setHolidayForm((f) => ({ ...f, type: e.target.value as 'national' | 'regional' | 'optional' }))}
                  style={{
                    width: '100%',
                    paddingLeft: 48,
                    paddingRight: 40,
                    paddingTop: 14,
                    paddingBottom: 14,
                    background: '#f3f4f6',
                    border: '1.5px solid transparent',
                    borderRadius: 14,
                    fontSize: 15,
                    color: 'var(--text, #1f2937)',
                    outline: 'none',
                    appearance: 'none',
                    // @ts-ignore
                    WebkitAppearance: 'none',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="national">National</option>
                  <option value="regional">Regional</option>
                  <option value="optional">Optional</option>
                </select>
                <span style={{ position: 'absolute', right: 16, pointerEvents: 'none', display: 'flex' }}>
                  <Icon name="chevron_down" size={16} color="#9ca3af" />
                </span>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Apply Leave Modal */}
      {modal.type === 'apply_leave' && (
        <Modal
          title="Apply for leave"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal}>Cancel</Button>
              <Button
                variant="accent"
                disabled={submitMutation.isPending}
                onClick={() => {
                  if (!leaveForm.startDate || !leaveForm.endDate || !leaveForm.reason) {
                    toast('Please fill all fields', 'warn'); return;
                  }
                  if (leaveForm.endDate < leaveForm.startDate) {
                    toast('"To" date cannot be before "From" date', 'warn'); return;
                  }
                  submitMutation.mutate({
                    employeeId: myId,
                    leaveTypeId: effectiveLeaveTypeId,
                    startDate: leaveForm.startDate,
                    endDate: leaveForm.endDate,
                    days: calcDays(),
                    reason: leaveForm.reason,
                  });
                }}
              >Submit request</Button>
            </>
          }
        >
          <div className="form-row">
            <label>Leave type</label>
            <select
              className="t-input"
              value={effectiveLeaveTypeId}
              onChange={(e) => setLeaveForm((f) => ({ ...f, leaveTypeId: e.target.value }))}
            >
              {leaveTypes.map((lt) => {
                const bal = leaveBalances.find((b) => b.leaveTypeId === lt.id)?.balance ?? 0;
                return <option key={lt.id} value={lt.id}>{lt.name} ({bal} days remaining)</option>;
              })}
            </select>
          </div>
          <div className="form-grid">
            <div className="form-row">
              <label>From</label>
              <DatePicker value={leaveForm.startDate} min={today} disableWeekends onChange={(v) => setLeaveForm((f) => ({ ...f, startDate: v, endDate: f.endDate && f.endDate < v ? '' : f.endDate }))} placeholder="From date" />
            </div>
            <div className="form-row">
              <label>To</label>
              <DatePicker value={leaveForm.endDate} min={leaveForm.startDate || today} disableWeekends onChange={(v) => setLeaveForm((f) => ({ ...f, endDate: v }))} placeholder="To date" />
            </div>
          </div>
          {calcDays() > 0 && (
            <Alert variant="info" style={{ marginBottom: 14 }}>{calcDays()} day{calcDays() > 1 ? 's' : ''} selected</Alert>
          )}
          <div className="form-row">
            <label>Reason</label>
            <textarea className="t-input" rows={3} style={{ height: 'auto', padding: '8px 10px', resize: 'vertical' }} value={leaveForm.reason} onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Personal appointment — optional but helps your manager plan." />
          </div>
        </Modal>
      )}
    </div>
  );
}
